import { Component } from '@theme/component';
import { clamp, prefersReducedMotion } from '@theme/utilities';
import { getScrollEventTarget, getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * A custom element that scrolls two rows of a gallery in opposite directions,
 * driven by how far the section has scrolled through the viewport, with a
 * lerp-based lag so movement settles smoothly after each scroll event.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [row1]
 * @property {HTMLElement} [row2]
 *
 * @extends Component<Refs>
 */
class ScrollGalleryComponent extends Component {
  /** @type {number} Percent of a row's own width it travels, in each direction. */
  #distance = 25;

  /** @type {number} Lerp factor applied per animation frame (0-1). */
  #ease = 0.12;

  #targetProgress = 0;
  #currentProgress = 0;

  /** @type {number | null} */
  #rafId = null;

  #intersecting = false;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#distance = parseFloat(this.dataset.scrollDistance || '') || this.#distance;
    this.#ease = parseFloat(this.dataset.ease || '') || this.#ease;

    if (prefersReducedMotion()) return;

    this.#observeIntersection();
    this.#bindScroll();
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#unbindScroll();
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#stopLoop();
  }

  #observeIntersection() {
    this.#observer = new IntersectionObserver(this.#handleIntersection, {
      root: getIntersectionRoot(),
      // Start/stop the render loop a little before the section enters/exits the viewport,
      // so the first frame after it becomes visible isn't already mid-animation.
      rootMargin: '50% 0px 50% 0px',
    });
    this.#observer.observe(this);
  }

  #bindScroll() {
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  #unbindScroll() {
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = null;
  }

  /**
   * The scroll container/root switches between `.page-wrapper` (desktop) and
   * `document` (mobile) at the squeeze breakpoint, so bindings must be redone.
   */
  #handleBreakpointChange = () => {
    this.#unbindScroll();
    this.#observer?.disconnect();
    this.#observeIntersection();
    this.#bindScroll();
  };

  #handleIntersection = (entries) => {
    const entry = entries[entries.length - 1];
    if (!entry) return;

    this.#intersecting = entry.isIntersecting;

    if (this.#intersecting) {
      this.#updateTarget();
      this.#startLoop();
    }
  };

  #handleScroll = () => {
    if (!this.#intersecting) return;

    this.#updateTarget();
    this.#startLoop();
  };

  /**
   * Progress is 0 when the section's top edge touches the viewport's bottom edge
   * (about to enter) and 1 when its bottom edge touches the viewport's top edge
   * (about to exit) — i.e. how far the section has scrolled through the viewport.
   */
  #updateTarget() {
    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const raw = (viewportHeight - rect.top) / (viewportHeight + rect.height);

    this.#targetProgress = clamp(raw, 0, 1);
  }

  #startLoop() {
    if (this.#rafId !== null) return;

    const step = () => {
      const delta = this.#targetProgress - this.#currentProgress;

      if (Math.abs(delta) < 0.0005) {
        this.#currentProgress = this.#targetProgress;
        this.#applyProgress();
        this.#rafId = null;
        return;
      }

      this.#currentProgress += delta * this.#ease;
      this.#applyProgress();
      this.#rafId = requestAnimationFrame(step);
    };

    this.#rafId = requestAnimationFrame(step);
  }

  #stopLoop() {
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
    this.#rafId = null;
  }

  #applyProgress() {
    const { row1, row2 } = this.refs;
    const progress = this.#currentProgress;

    if (row1) row1.style.transform = `translate3d(${(-progress * this.#distance).toFixed(3)}%, 0, 0)`;
    if (row2) row2.style.transform = `translate3d(${(-(1 - progress) * this.#distance).toFixed(3)}%, 0, 0)`;
  }
}

if (!customElements.get('scroll-gallery-component')) {
  customElements.define('scroll-gallery-component', ScrollGalleryComponent);
}
